import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import PhoneLinkCard from './PhoneLinkCard'
import { isSignedIn } from '@/lib/baba-auth'
import {
  markPhoneLinkPromptSeen,
  readPhoneLinkPromptPending,
  readSeenPhoneLinkAccounts,
  shouldShowPhoneLinkPrompt,
} from '@/lib/phoneLink'
import { useAccountLinkStatusQuery } from '../store/babaApi'

/**
 * The one-time "Use baba on Your Phone" panel, shown once per account after a
 * successful web sign-in.
 *
 * Three deliberate limits:
 *
 *  - It is a slim card above the routed page, not a modal. Someone who just
 *    signed in came to translate something; the panel must not stand in front
 *    of that.
 *  - Desktop only. Offering a QR to scan "with your phone" to someone already
 *    holding their phone is nonsense, and MobileAppPromo already owns the
 *    phone-web pitch.
 *  - Never for an account whose phone is already linked, which is why it waits
 *    for the link-status request instead of rendering optimistically.
 *  - Never on Settings › Account Sync, which already owns this exact card. Two
 *    QRs for the same code on one screen is a question ("which one?"), not a
 *    convenience.
 *
 * Sign-in reloads the page, so the trigger cannot be a callback — it is a
 * sessionStorage marker written at the moment of sign-in (see
 * markPhoneLinkPromptPending). That also bounds the panel to the session the
 * sign-in happened in; dismissing it additionally remembers the account.
 */
export default function PhoneLinkPrompt() {
  // Read once on mount: dismissing must not race a re-read of the same marker.
  const [pendingEmail] = useState(() => readPhoneLinkPromptPending())
  const seenEmails = useMemo(() => readSeenPhoneLinkAccounts(), [])
  const [dismissed, setDismissed] = useState(false)
  // Set once the phone is found linked: the panel can never show again.
  const [retired, setRetired] = useState(false)
  const [isWide, setIsWide] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 768)

  const { pathname } = useLocation()
  const signedIn = isSignedIn()
  const onAccountSyncPage = pathname.startsWith('/settings/account-sync')
  // Polling only while the panel can still appear. An account that has
  // already seen it, or whose phone is linked, used to keep asking
  // /account-link/status every five seconds for the rest of the tab session.
  const alreadySeen = !!pendingEmail && seenEmails.includes(pendingEmail.trim().toLowerCase())
  const active = !!pendingEmail && signedIn && !dismissed && !retired && !alreadySeen && isWide && !onAccountSyncPage

  const { data: linkStatus } = useAccountLinkStatusQuery(undefined, {
    skip: !active,
    // Matches the Account Sync page: the panel should disappear on its own the
    // moment the code is accepted on the phone.
    pollingInterval: 5000,
    skipPollingIfUnfocused: true,
  })

  useEffect(() => {
    const onResize = () => setIsWide(window.innerWidth >= 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Linking succeeded: retire the panel for this account for good.
  useEffect(() => {
    if (!linkStatus?.linked || !pendingEmail) return
    markPhoneLinkPromptSeen(pendingEmail)
    setRetired(true)
  }, [linkStatus?.linked, pendingEmail])

  const visible =
    active &&
    shouldShowPhoneLinkPrompt({
      pendingEmail,
      seenEmails,
      linked: linkStatus ? linkStatus.linked : null,
    })

  if (!visible) return null

  return (
    <div className="shrink-0" style={{ padding: '14px 20px 0' }}>
      <div className="mx-auto" style={{ maxWidth: 960 }}>
        <PhoneLinkCard
          placement="post_signin_panel"
          variant="panel"
          onDismiss={() => {
            if (pendingEmail) markPhoneLinkPromptSeen(pendingEmail)
            setDismissed(true)
          }}
        />
      </div>
    </div>
  )
}
